// src/services/notificationService.js

import {
    auth,
    database,
    getToken,
    onMessage,
    onTokenRefresh
} from '../firebase';

import {
    ref as dbRef,
    push,
    set,
    get,
    onValue,
    off
} from 'firebase/database';

class NotificationService {
    constructor() {
        this.isSupported =
            typeof window !== 'undefined' &&
            'Notification' in window &&
            'serviceWorker' in navigator;

        this.permission = this.isSupported
            ? Notification.permission
            : 'denied';

        this.registration = null;

        this.listeners = new Map();

        this.fcmUnsubscribe = null;

        this.initialized = false;
    }

    /*
    |--------------------------------------------------------------------------
    | INIT
    |--------------------------------------------------------------------------
    */

    async initialize() {
        if (this.initialized) return true;

        if (!this.isSupported) {
            console.warn('Notifications not supported');
            return false;
        }

        try {
            // Reuse existing SW if already registered — never unregister
            const existing = await navigator.serviceWorker.getRegistration('/');

            this.registration = existing ?? await navigator.serviceWorker.register(
                '/firebase-messaging-sw.js',
                { scope: '/' }
            );

            await navigator.serviceWorker.ready;

            // Foreground FCM handling (single source of truth)
            this.fcmUnsubscribe = await onMessage((payload) => {
                this._handleIncomingFCM(payload);
            });

            this.initialized = true;

            return true;
        } catch (error) {
            console.error('Notification init failed:', error);
            return false;
        }
    }

    /*
    |--------------------------------------------------------------------------
    | PERMISSION
    |--------------------------------------------------------------------------
    */

    async requestPermission() {
        if (!this.isSupported) return 'denied';

        this.permission = Notification.permission;

        if (this.permission === 'granted') return 'granted';
        if (this.permission === 'denied') return 'denied';

        try {
            const result = await Notification.requestPermission();
            this.permission = result;
            return result;
        } catch (error) {
            console.error('Permission error:', error);
            return 'denied';
        }
    }

    /*
    |--------------------------------------------------------------------------
    | FCM TOKEN
    |--------------------------------------------------------------------------
    */

    async initFCM() {
        const currentPermission = Notification.permission;  // ← always live
    
        if (currentPermission !== 'granted') {
            console.warn('[initFCM] Skipping — permission not granted:', currentPermission);
            return null;
        }

        if (!this.registration) {
            this.registration = await navigator.serviceWorker.register(
                '/firebase-messaging-sw.js'
            );
        }

        const vapidKey = 'BESYBWNaU4jnZKPbdLcX5OdG4ho1D-h-K5I2uPYBsKuJCLtL6mekQ8vxotZLFmNYLzeOh9vS3iXIxVDaEnRv-LU';

        const token = await getToken({
            vapidKey,
            serviceWorkerRegistration: this.registration
        });

        if (!token) return null;

        // ✅ Use saveTokenToUser — writes to fcmTokens
        await this.saveTokenToUser(token);

        // Keep token fresh if FCM rotates it mid-session
        onTokenRefresh(async () => {
            try {
                const newToken = await getToken({
                    vapidKey,
                    serviceWorkerRegistration: this.registration
                });
                if (newToken) await this.saveTokenToUser(newToken);
            } catch (err) {
                console.error('Token refresh failed:', err);
            }
        });

        return token;
    }

    /*
    |--------------------------------------------------------------------------
    | FIREBASE TOKEN STORAGE (KEEP SIMPLE)
    |--------------------------------------------------------------------------
    */

    async saveTokenToUser(token) {
        const user = auth.currentUser;
        if (!user || !token) return;

        // Use last 16 chars as a stable key for this token
        const tokenKey = token.slice(-16);

        const tokenRef = dbRef(database, `users/${user.uid}/fcmTokens/${tokenKey}`);

        await set(tokenRef, {
            token,
            updatedAt: Date.now(),
            platform: navigator.userAgent
        });
    }

    /*
    |--------------------------------------------------------------------------
    | LOCAL NOTIFICATION DISPLAY (ONLY ONE PATH)
    |--------------------------------------------------------------------------
    */

    showNotification(title, options = {}) {
        if (this.permission !== 'granted') return;

        const defaultOptions = {
            body: '',
            icon: '/Images/eFILMHUB LOGO-ICON - OR.png',
            badge: '/Images/eFILMHUB LOGO-ICON - OR.png',
            //tag: 'efilmhub',
            data: {},
            requireInteraction: false
        };

        const payload = {
            ...defaultOptions,
            ...options
        };

        try {
            if (this.registration?.showNotification) {
                this.registration.showNotification(title, payload);
            } else {
                new Notification(title, payload);
            }
        } catch (error) {
            console.error('Notification error:', error);
        }
    }

    /*
    |--------------------------------------------------------------------------
    | FCM HANDLER (SINGLE SOURCE OF TRUTH)
    |--------------------------------------------------------------------------
    */

    _handleIncomingFCM(payload) {
        
        console.log('FCM received:', JSON.stringify(payload));

        if (payload?.data?.silent === 'true') return;

        const d = payload?.data || {};
        const title = d.title || payload?.notification?.title || 'eFilmHub';
        const body = d.body || payload?.notification?.body || 'New notification';

        this.showNotification(title, {
            body,
            data: d
        });
    }

    /*
    |--------------------------------------------------------------------------
    | REALTIME DB NOTIFICATIONS (DISPLAY ONLY - NO CREATION)
    |--------------------------------------------------------------------------
    */

    subscribeToNotifications(callback) {
        const user = auth.currentUser;
        if (!user) return null;

        // HARD STOP: prevent duplicates
        const existing = this.listeners.get(user.uid);
        if (existing) {
            existing(); // unsubscribe old
            this.listeners.delete(user.uid);
        }

        const ref = dbRef(database, `notifications/${user.uid}`);

        const unsubscribe = onValue(ref, (snapshot) => {
            const list = [];

            snapshot.forEach((child) => {
                list.push({
                    id: child.key,
                    ...child.val()
                });
            });

            callback(list);
        });

        this.listeners.set(user.uid, unsubscribe);

        return unsubscribe;
    }

    unsubscribeFromNotifications(userId) {
        const unsub = this.listeners.get(userId);

        if (unsub) {
            const ref = dbRef(
                database,
                `notifications/${userId}`
            );

            off(ref);

            this.listeners.delete(userId);
        }
    }

    /*
    |--------------------------------------------------------------------------
    | MARK AS READ
    |--------------------------------------------------------------------------
    */

    async markAsRead(notificationId) {
        const user = auth.currentUser;
        if (!user) return;

        const ref = dbRef(
            database,
            `notifications/${user.uid}/${notificationId}`
        );

        const snap = await get(ref);

        if (!snap.exists()) return;

        await set(ref, {
            ...snap.val(),
            read: true,
            readAt: Date.now()
        });
    }

    async markAllAsRead() {
        const user = auth.currentUser;
        if (!user) return;

        const ref = dbRef(
            database,
            `notifications/${user.uid}`
        );

        const snap = await get(ref);

        if (!snap.exists()) return;

        const updates = [];

        snap.forEach((child) => {
            updates.push(
                set(
                    dbRef(
                        database,
                        `notifications/${user.uid}/${child.key}`
                    ),
                    {
                        ...child.val(),
                        read: true,
                        readAt: Date.now()
                    }
                )
            );
        });

        await Promise.all(updates);
    }

    /*
    |--------------------------------------------------------------------------
    | CLEANUP
    |--------------------------------------------------------------------------
    */

    destroy() {
        if (this.fcmUnsubscribe) {
            this.fcmUnsubscribe();
        }

        this.listeners.clear();
        this.initialized = false;
    }
}

export const notificationService =
    new NotificationService();

if (typeof window !== 'undefined') {
    window.notificationService =
        notificationService;
} 